import apiClient from '@/shared/services/apiClient'
import type {
  ApiResponse,
  SyllabusResponse,
  SyllabiResponse,
  NodeResponse,
  NodesResponse,
  HierarchyTreeResponse,
  NodeType,
} from '../types'

class SyllabusService {
  private readonly baseUrl = '/api/v1/syllabus'

  /**
   * Get all syllabi
   * @param isActive - Filter by active status (optional)
   */
  async getAllSyllabi(isActive?: boolean): Promise<ApiResponse<SyllabiResponse>> {
    const params = isActive !== undefined ? { isActive } : {}
    const response = await apiClient.get<ApiResponse<SyllabiResponse>>(this.baseUrl, { params })
    return response.data
  }

  /**
   * Get syllabus by code
   * @param syllabusCode - Syllabus code (e.g., "NEET_UG")
   */
  async getSyllabusByCode(syllabusCode: string): Promise<ApiResponse<SyllabusResponse>> {
    const response = await apiClient.get<ApiResponse<SyllabusResponse>>(
      `${this.baseUrl}/${syllabusCode}`
    )
    return response.data
  }

  /**
   * Get all nodes for a syllabus
   * @param syllabusCode - Syllabus code
   * @param filters - Optional filters (nodeType, parentNodeCode, isActive)
   */
  async getNodesBySyllabus(
    syllabusCode: string,
    filters?: {
      nodeType?: NodeType
      parentNodeCode?: string
      isActive?: boolean
    }
  ): Promise<ApiResponse<NodesResponse>> {
    const response = await apiClient.get<ApiResponse<NodesResponse>>(
      `${this.baseUrl}/${syllabusCode}/nodes`,
      { params: filters }
    )
    return response.data
  }

  /**
   * Get node by code
   * @param syllabusCode - Syllabus code
   * @param nodeCode - Node code
   */
  async getNodeByCode(
    syllabusCode: string,
    nodeCode: string
  ): Promise<ApiResponse<NodeResponse>> {
    const response = await apiClient.get<ApiResponse<NodeResponse>>(
      `${this.baseUrl}/${syllabusCode}/nodes/${nodeCode}`
    )
    return response.data
  }

  /**
   * Get child nodes of a parent node
   * @param syllabusCode - Syllabus code
   * @param nodeCode - Parent node code
   */
  async getChildNodes(
    syllabusCode: string,
    nodeCode: string
  ): Promise<ApiResponse<NodesResponse>> {
    const response = await apiClient.get<ApiResponse<NodesResponse>>(
      `${this.baseUrl}/${syllabusCode}/nodes/${nodeCode}/children`
    )
    return response.data
  }

  /**
   * Get full hierarchical tree for a syllabus
   * @param syllabusCode - Syllabus code (e.g., "NEET_UG")
   */
  async getSyllabusHierarchy(syllabusCode: string): Promise<ApiResponse<HierarchyTreeResponse>> {
    const response = await apiClient.get<ApiResponse<HierarchyTreeResponse>>(
      `${this.baseUrl}/${syllabusCode}/hierarchy`
    )
    return response.data
  }
}

export default new SyllabusService()
